import moment from "moment";

function workoutProgramMeta(workout) {
  const length = parseInt(workout?.programLength, 10);
  if (!Number.isFinite(length) || length <= 1) return null;
  const week = parseInt(workout?.currentWeek, 10);
  const currentWeek = Number.isFinite(week) && week > 0 ? week : 1;
  return { currentWeek, programLength: length };
}

export function workoutsInWeek(workouts, weekContainingMoment) {
  const ws = weekContainingMoment.clone().startOf("week");
  const we = weekContainingMoment.clone().endOf("week");
  return (workouts || []).filter((w) => {
    const d = moment.unix(w.date);
    return d.isBetween(ws, we, "day", "[]");
  });
}

function programStatusFromWorkoutSubset(workouts) {
  const metas = (workouts || []).map(workoutProgramMeta).filter(Boolean);
  if (metas.length === 0) return null;

  const maxCurrentWeek = Math.max(...metas.map((m) => m.currentWeek));
  const atMax = metas.filter((m) => m.currentWeek === maxCurrentWeek);
  const programLength = Math.max(...atMax.map((m) => m.programLength));
  const weeksRemaining = Math.max(0, programLength - maxCurrentWeek);

  return {
    currentWeek: maxCurrentWeek,
    programLength,
    weeksRemaining,
  };
}

function programWeekBounds(workouts) {
  const metas = (workouts || []).map(workoutProgramMeta).filter(Boolean);
  if (metas.length === 0) return null;
  const weeks = metas.map((m) => m.currentWeek);
  return {
    minWeek: Math.min(...weeks),
    maxWeek: Math.max(...weeks),
  };
}

/**
 * Next week starts week 1 of a new block instead of continuing this week's
 * sequence (which would be maxWeek + 1).
 */
function nextWeekStartsNewProgramBlock(thisWeekWorkouts, nextWeekWorkouts) {
  const nextBounds = programWeekBounds(nextWeekWorkouts);
  if (!nextBounds || nextBounds.minWeek !== 1) return false;

  const thisBounds = programWeekBounds(thisWeekWorkouts);
  if (!thisBounds) return true;

  return thisBounds.maxWeek + 1 !== nextBounds.minWeek;
}

/**
 * Prefer next week's program metadata when:
 * - next week starts a new block at week 1 (even if this week still has the
 *   tail of the old program), or
 * - this week is empty and the prior block is complete.
 */
function shouldUseNextWeekProgramStatus({
  thisWeekWorkouts,
  nextWeekWorkouts,
  status,
  prevWeekStatus,
  nextWeekStatus,
}) {
  if (!nextWeekStatus || nextWeekWorkouts.length === 0) return false;

  if (nextWeekStartsNewProgramBlock(thisWeekWorkouts, nextWeekWorkouts)) {
    return true;
  }

  if (thisWeekWorkouts.length > 0) return false;

  if (!status) return true;
  if (status.weeksRemaining === 0) return true;
  if (prevWeekStatus?.weeksRemaining === 0 && status.weeksRemaining === 0) {
    return true;
  }
  return false;
}

/**
 * Program progress: workouts in the calendar week containing
 * `referenceMoment` (highest currentWeek). If none, use the previous week only.
 * If that block is complete and next week already has workouts, use next week's
 * program metadata for display.
 */
export function clientProgramStatusFromWorkouts(
  workouts,
  referenceMoment = moment()
) {
  const ref = moment.isMoment(referenceMoment)
    ? referenceMoment
    : moment(referenceMoment);

  const thisWeekWorkouts = workoutsInWeek(workouts, ref);
  const prevWeekWorkouts = workoutsInWeek(
    workouts,
    ref.clone().subtract(1, "week")
  );
  const nextWeekWorkouts = workoutsInWeek(workouts, ref.clone().add(1, "week"));

  const thisWeekStatus = programStatusFromWorkoutSubset(thisWeekWorkouts);
  const prevWeekStatus = programStatusFromWorkoutSubset(prevWeekWorkouts);
  const nextWeekStatus = programStatusFromWorkoutSubset(nextWeekWorkouts);
  const hasNextWeekProgram = nextWeekWorkouts.length > 0;

  let status =
    thisWeekWorkouts.length > 0 ? thisWeekStatus : prevWeekStatus;
  let statusFromNextWeek = false;

  if (
    hasNextWeekProgram &&
    shouldUseNextWeekProgramStatus({
      thisWeekWorkouts,
      nextWeekWorkouts,
      status,
      prevWeekStatus,
      nextWeekStatus,
    })
  ) {
    status = nextWeekStatus;
    statusFromNextWeek = true;
  }

  if (!status) {
    return {
      currentWeek: null,
      programLength: null,
      weeksRemaining: null,
      hasNextWeekProgram,
      statusFromNextWeek: false,
    };
  }

  return {
    ...status,
    hasNextWeekProgram,
    statusFromNextWeek,
  };
}

/** MUI palette path for program urgency text color. */
export function programUrgencyColor(weeksRemaining) {
  if (weeksRemaining == null) return "text.secondary";
  if (weeksRemaining <= 1) return "error.main";
  if (weeksRemaining < 4) return "warning.main";
  return "success.main";
}

/** Lower = needs a program sooner. Used for client list ordering. */
export function clientProgramSortKey(status) {
  const weeks = status?.weeksRemaining;
  return weeks == null ? Number.POSITIVE_INFINITY : weeks;
}

export function programUrgencyLabel(weeksRemaining) {
  if (weeksRemaining == null) return "No program";
  if (weeksRemaining === 0) return "Program complete";
  if (weeksRemaining === 1) return "1 week left";
  return `${weeksRemaining} weeks left`;
}
